import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Plus, MapPin, Buildings, Trash, Clock, CheckCircle, Prohibit } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/job-descriptions`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load job descriptions');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (job, e) => {
    e.preventDefault();
    e.stopPropagation();
    const newStatus = job.status === 'Closed' ? 'Open' : 'Closed';
    try {
      const { data } = await axios.patch(`${API}/job-descriptions/${job.id}/status`, { status: newStatus });
      setJobs((prev) => prev.map((j) => (j.id === job.id ? data : j)));
      toast.success(`Job marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('Failed to update job status');
    }
  };

  const filteredJobs = statusFilter === 'All' ? jobs : jobs.filter((j) => (j.status || 'Open') === statusFilter);

  const deleteJob = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this job description?')) {
      return;
    }

    try {
      await axios.delete(`${API}/job-descriptions/${id}`);
      toast.success('Job description deleted');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job description');
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 bg-white border border-slate-200 rounded-lg skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="jobs-list-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Job Descriptions
            </h1>
            <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
              Manage all open roles and job requirements
            </p>
          </div>
          <Link
            to="/jobs/new"
            className="flex items-center space-x-2 btn-primary px-5 py-2.5 rounded-md text-sm font-medium"
            data-testid="create-job-button"
          >
            <Plus size={18} weight="bold" />
            <span>Add Job Description</span>
          </Link>
        </div>

        {jobs.length > 0 && (
          <div className="inline-flex bg-slate-100 p-1 rounded-lg mb-6" data-testid="job-status-filter">
            {['All', 'Open', 'Closed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  statusFilter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`job-status-filter-${s.toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {jobs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-jobs-state">
            <div className="inline-flex bg-blue-50 p-4 rounded-lg mb-4">
              <Briefcase size={48} weight="duotone" className="text-[#002FA7]" />
            </div>
            <p className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No job descriptions yet
            </p>
            <p className="text-base text-slate-600 mb-6">
              Add your first job description to start matching candidates to roles.
            </p>
            <Link
              to="/jobs/new"
              className="inline-flex items-center space-x-2 btn-primary px-6 py-2 rounded-md text-sm font-medium"
            >
              <Plus size={18} weight="bold" />
              <span>Add First Job</span>
            </Link>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-filtered-jobs-state">
            <p className="text-base text-slate-600">No {statusFilter.toLowerCase()} job descriptions.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-grid">
            {filteredJobs.map((job) => {
              const isClosed = job.status === 'Closed';
              return (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-blue-50 p-2.5 rounded-md">
                    <Briefcase size={20} weight="duotone" className="text-[#002FA7]" />
                  </div>
                  <div className="flex items-center space-x-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                        isClosed ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'
                      }`}
                      data-testid={`job-status-${job.id}`}
                    >
                      {isClosed ? 'Closed' : 'Open'}
                    </span>
                    <button
                      onClick={(e) => toggleStatus(job, e)}
                      className="p-1.5 text-slate-400 hover:text-[#002FA7] hover:bg-blue-50 rounded transition-colors"
                      data-testid={`toggle-status-${job.id}`}
                      title={isClosed ? 'Mark as Open' : 'Mark as Closed'}
                    >
                      {isClosed ? <CheckCircle size={16} /> : <Prohibit size={16} />}
                    </button>
                    <button
                      onClick={(e) => deleteJob(job.id, e)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      data-testid={`delete-job-${job.id}`}
                      title="Delete"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-lg font-medium text-slate-900 mb-2 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {job.title || 'Untitled Role'}
                </h3>

                <div className="space-y-1.5 mb-4">
                  {job.department && (
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Buildings size={14} />
                      <span>{job.department}</span>
                    </div>
                  )}
                  {job.location && (
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <MapPin size={14} />
                      <span>{job.location}</span>
                    </div>
                  )}
                  {job.experience_required && (
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Clock size={14} />
                      <span>{job.experience_required}</span>
                    </div>
                  )}
                </div>

                {job.description && (
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {job.description}
                  </p>
                )}

                {job.employment_type && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <span className="inline-flex px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                      {job.employment_type}
                    </span>
                  </div>
                )}
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsList;
